<div class="space-y-6">
  @if (!twoFactorEnabled) {
  <div
    class="rounded-2xl border border-gray-200 bg-gray-50 px-6 py-5 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
    <div class="flex items-start gap-4">
      <div
        class="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-6 w-6">
          <path fill-rule="evenodd"
            d="M12.516 2.17a.75.75 0 00-1.032 0 11.209 11.209 0 01-7.877 3.08.75.75 0 00-.722.515A12.74 12.74 0 002.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.749.749 0 00.374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 00-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08zm3.094 8.016a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
            clip-rule="evenodd" />
        </svg>
      </div>
      <div class="space-y-1">
        <h3 class="text-sm font-semibold text-gray-900 dark:text-white/90">Two-Factor Authentication (2FA)</h3>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Two-factor authentication adds an extra layer of security to your account. Scan the QR code with an
          authenticator app and enter the 6-digit code to finish enabling 2FA for your account.
        </p>
        @if (emailOtpEnabled) {
        <p class="text-xs text-amber-600 dark:text-amber-400 mt-1">
          Email OTP is currently enabled. Disable Email OTP first if you want to use 2FA instead. Only one second
          verification method can be active at a time.
        </p>
        }
      </div>
    </div>

    @if (!emailOtpEnabled) {
    @if (!showSetupPrompt) {
    <div class="mt-4 flex justify-end">
      <app-button variant="primary" size="sm" (btnClick)="openSetupPrompt()" [disabled]="loading">
        Enable 2FA
      </app-button>
    </div>
    } @else {
    <div class="mt-4 space-y-2">
      <div>
        <label for="2fa-setup-password" class="block text-xs font-medium text-gray-700 mb-1 dark:text-gray-300">Current
          Password</label>
      </div>
      <div class="flex items-center gap-4">
        <div class="flex-1">
          <app-input-field id="2fa-setup-password" type="password" name="password"
            placeholder="Enter your current password" [value]="password" (valueChange)="password = $event.toString()"
            [disabled]="loading" />
        </div>
        <button type="button" (click)="cancelSetupPrompt()" class="text-sm text-gray-500 hover:text-gray-700">
          Cancel
        </button>
        <app-button variant="primary" size="sm" (btnClick)="loadSetup()" [disabled]="loading || !password.trim()">
          @if (loading) {
          <span class="flex items-center gap-2">
            <span class="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
            Starting...
          </span>
          } @else {
          Enable
          }
        </app-button>
      </div>
    </div>
    }
    } @else {
    <div
      class="mt-4 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-xs text-amber-700 dark:border-amber-500/30 dark:bg-amber-900/20 dark:text-amber-200">
      To enable 2FA, first go to Email OTP settings and turn off Email OTP. Only one second verification method can be
      enabled at a time.
    </div>
    }
  </div>
  }

  @if (errorMessage && !showSetupPrompt) {
  <div class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 mt-4">
    {{ errorMessage }}
  </div>
  }

  <app-modal [isOpen]="showSetupModal" (close)="closeSetupModal()"
    [className]="currentStep === 'confirm-exit' ? 'max-w-md' : 'max-w-2xl'"
    [showCloseButton]="currentStep !== 'confirm-exit'">

    @if (currentStep === 'confirm-exit') {
    <div class="flex flex-col items-center text-center space-y-0 pb-4 pt-1">
      <div class="flex items-center justify-center">
        <img src="/images/graphic/alert-graphic.png" alt="Warning" class="h-[120px] w-[120px] object-contain" />
      </div>
      <div class="space-y-1">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white/90 -mt-5">Stop Setting Up?</h3>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Your progress won't be saved. You need to finish verifying the code to enable 2FA.
        </p>
      </div>
      <div class="flex flex-col gap-3 w-full pt-4">
        <app-button variant="primary" size="sm" className="w-full justify-center" (btnClick)="cancelClose()">
          Continue Setup
        </app-button>
        <button type="button" (click)="confirmClose()"
          class="w-full py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors dark:text-red-400 dark:hover:bg-red-900/20">
          Exit & Discard
        </button>
      </div>
    </div>
    }

    <!-- NORMAL SETUP VIEWS -->
    @else {
    <div class="space-y-6">
      <!-- Centered Header -->
      <div class="text-center px-12">
        <h3 class="text-xl font-bold text-gray-900 dark:text-white/90">
          Setup Two-Factor Authentication
        </h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Using an authenticator app like Google Authenticator, Microsoft Authenticator, or any TOTP app.
        </p>
      </div>

      <!-- Step 1: Scan QR Code -->
      <div class="space-y-2">
        <h4 class="text-sm font-semibold text-blue-600 uppercase tracking-wider">Step 1</h4>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Scan the QR code below or manually enter the secret key into your authenticator app.
        </p>

        <!-- QR Card Container -->
        <div class="rounded-xl border border-gray-100 bg-gray-50 p-6 dark:border-gray-800 dark:bg-gray-900/40">
          <div class="flex flex-col md:flex-row gap-8 items-center">
            <!-- QR Code -->
            <div class="shrink-0 bg-white p-2 rounded-xl border border-gray-200">
              @if (qrImageUrl) {
              <img [src]="qrImageUrl" alt="Authenticator QR code"
                class="h-40 w-40 object-contain mix-blend-multiply dark:mix-blend-normal" loading="lazy" />
              }
            </div>

            <!-- Manual Entry Details -->
            <div class="flex-1 w-full min-w-0 space-y-4">
              <p class="text-xs font-bold text-gray-900 dark:text-white">Can't scan QR code? Enter the secret key
                manually.</p>

              <div class="space-y-3">
                <!-- Secret Key Card -->
                <div
                  class="bg-white rounded-xl border border-gray-200 px-4 py-3.5 dark:bg-gray-800 dark:border-gray-700 shadow-sm flex items-center justify-between gap-3">
                  <div class="min-w-0 flex-1">
                    <p class="text-xs text-gray-500 mb-1 dark:text-gray-400 font-medium">Secret Key</p>
                    <p class="font-mono text-sm text-gray-700 break-all dark:text-gray-300 tracking-wider">{{ secret ||
                      'Unavailable' }}</p>
                  </div>
                  <button type="button" (click)="copyToClipboard(secret, 'Secret key')"
                    class="shrink-0 px-4 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:text-white">
                    Copy
                  </button>
                </div>

                <!-- OTP URL Card -->
                <div
                  class="bg-white rounded-xl border border-gray-200 px-4 py-3.5 dark:bg-gray-800 dark:border-gray-700 shadow-sm flex items-center justify-between gap-3">
                  <div class="min-w-0 flex-1">
                    <p class="text-xs text-gray-500 mb-1 dark:text-gray-400 font-medium">OTP Auth URL</p>
                    <p class="font-mono text-xs text-gray-500 truncate dark:text-gray-400 leading-relaxed"
                      [title]="otpauthUrl">
                      {{ otpauthUrl || 'Unavailable' }}
                    </p>
                  </div>
                  <button type="button" (click)="copyToClipboard(otpauthUrl, 'OTP URL')"
                    class="shrink-0 px-4 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:text-white">
                    Copy
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 2: Verify Code -->
      <div class="space-y-3">
        <div>
          <h4 class="text-sm font-semibold text-blue-600 uppercase tracking-wider">Step 2</h4>
          <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Enter the 6-digit code you see in your authenticator app.
          </p>
        </div>

        <div class="space-y-2">
          <label for="verificationCode" class="block text-xs font-bold text-gray-900 dark:text-white">
            Authentication Code
          </label>

          <!-- Visual 6-Box Input -->
          <div class="relative max-w-sm">
            <input #codeInput id="verificationCode" type="text" inputmode="numeric" autocomplete="one-time-code"
              maxlength="6" class="absolute inset-0 w-full h-full opacity-0 cursor-text z-10 font-mono tracking-[1em]"
              [value]="verificationCode" (input)="verificationCode = codeInput.value.slice(0, 6)"
              [disabled]="verifying" />

            <div class="flex gap-3" aria-hidden="true">
              @for (box of [0, 1, 2, 3, 4, 5]; track box) {
              <div
                class="h-12 w-10 flex items-center justify-center rounded-lg border bg-white text-lg font-mono font-medium transition-all duration-200 dark:bg-gray-800"
                [ngClass]="{
                      'border-blue-500 ring-2 ring-blue-500/20': verificationCode.length === box,
                      'border-gray-200 dark:border-gray-700': verificationCode.length !== box,
                      'text-gray-900 dark:text-gray-100': verificationCode[box],
                      'text-gray-300': !verificationCode[box]
                    }">
                {{ verificationCode[box] }}
              </div>
              }
            </div>
          </div>
          <p class="text-xs text-gray-500 dark:text-gray-400">Codes refresh every 30 seconds in your authenticator app.
          </p>
        </div>

        @if (twoFactorError) {
        <p class="text-sm text-red-600">{{ twoFactorError }}</p>
        }
      </div>

      <div class="flex items-center gap-3 pt-2">
        @if (!forceSetupRequired) {
        <button type="button" (click)="closeSetupModal()"
          class="flex-1 px-4 py-2.5 text-sm font-medium text-blue-500 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 dark:bg-transparent dark:border-gray-700 dark:text-blue-400 dark:hover:bg-white/[0.05]">
          Discard
        </button>
        }

        <button type="button" (click)="verifyCode()" [disabled]="verifying || !verificationCode"
          [class]="forceSetupRequired
            ? 'w-full px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-blue-500/20 transition-all'
            : 'flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-blue-500/20 transition-all'">
          @if (verifying) {
          <span class="flex items-center justify-center gap-2">
            <span class="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
            Verifying...
          </span>
          } @else {
          Verify Code
          }
        </button>
      </div>

    </div>
    }
  </app-modal>

  @if (twoFactorEnabled) {
  <div class="rounded-2xl border border-gray-200 bg-gray-50 p-6 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
    <div class="flex items-center justify-between gap-3 mb-6">
      <div class="flex items-start gap-3">
        <div
          class="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-5 w-5">
            <path fill-rule="evenodd"
              d="M12.516 2.17a.75.75 0 00-1.032 0 11.209 11.209 0 01-7.877 3.08.75.75 0 00-.722.515A12.74 12.74 0 002.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.749.749 0 00.374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 00-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08zm3.094 8.016a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
              clip-rule="evenodd" />
          </svg>
        </div>
        <div>
          <h3 class="text-lg font-bold text-gray-900 dark:text-white/90">
            {{ forceSetupRequired ? 'Two-Factor Authentication' : 'Disable Two-Factor' }}
          </h3>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            {{ forceSetupRequired ?
            'Two-factor authentication is required for your account.' :
            'Turn off 2FA by confirming your current password. You can re-enable anytime.' }}
          </p>
        </div>
      </div>
      <span
        class="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700 dark:bg-green-500/20 dark:text-green-300">
        <span class="h-1.5 w-1.5 rounded-full bg-green-500"></span>
        Enabled
      </span>
    </div>

    @if (!forceSetupRequired) {
    <div class="flex flex-wrap items-end gap-4">
      <div class="flex-1 min-w-[200px]">
        <label for="2fa-disable-password"
          class="block text-xs font-medium text-gray-700 mb-1 dark:text-gray-300">Current Password</label>
        <app-input-field id="2fa-disable-password" type="password" name="disablePassword"
          placeholder="Enter password to disable" className="w-full" [value]="disablePassword"
          (valueChange)="disablePassword = $event.toString()" [disabled]="disabling" />
      </div>
      <div class="pb-[1px]">
        <app-button variant="outline" size="sm"
          className="text-red-600 border-red-200 hover:bg-red-50 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-900/20"
          (btnClick)="disableTwoFactor()" [disabled]="disabling">
          @if (disabling) {
          <span class="flex items-center gap-2">
            Disabling...
          </span>
          } @else {
          Disable 2FA
          }
        </app-button>
      </div>
    </div>
    } @else {
    <div
      class="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-xs text-amber-700 dark:border-amber-500/30 dark:bg-amber-900/20 dark:text-amber-200">
      Your account requires authenticator-based 2FA. Disabling is not available.
    </div>
    }
  </div>
  }

  <div
    class="rounded-2xl border border-gray-200 bg-gray-50 px-6 py-5 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
    <div class="flex items-start gap-4">
      <div
        class="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-6 w-6">
          <path d="M1.5 8.67v8.58a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3V8.67l-8.928 5.493a1.75 1.75 0 0 1-1.644 0L1.5 8.67z" />
          <path
            d="M22.5 6.908V6.75a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3v.158l9.714 5.978a1.5 1.5 0 0 0 1.572 0L22.5 6.908z" />
        </svg>
      </div>
      <div class="flex-1 space-y-3">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="space-y-1">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white/90">
              Email One-Time Password (Email OTP)
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              In addition to authenticator apps, you can verify your login using a one-time code sent to your
              registered email address. On the two-factor verification screen, choose to receive a code by email and
              enter the 6-digit code you receive to continue.
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-500">
              Make sure your email address is up to date and that you can access it before relying on Email OTP.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="pt-4 mt-1">
      @if (!emailOtpEnabled && !twoFactorEnabled) {
      @if (!isEmailOtpPromptOpen) {
      <div class="flex flex-wrap items-center justify-end gap-3">
        <app-button size="sm" (btnClick)="openEmailOtpPrompt()">
          Enable Email OTP
        </app-button>
      </div>
      }
      } @else if (twoFactorEnabled) {
      <div class="space-y-2">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Two-Factor Authentication (2FA) is currently
          <span class="font-semibold text-green-700 dark:text-green-300">enabled</span>.
        </p>
        <p class="text-xs text-amber-600 dark:text-amber-400">
          To use Email OTP instead of 2FA, first disable 2FA in the card above. Only one second verification method can
          be active at a time.
        </p>
      </div>
      } @else {
      <div class="space-y-3">
        @if (!forceSetupRequired) {
        <div class="flex items-center justify-between gap-3">
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Email OTP is <span class="font-semibold text-green-700 dark:text-green-300">enabled</span>. Enter
            your current password to turn it off.
          </p>
        </div>
        <div class="flex flex-wrap items-end gap-4">
          <div class="flex-1 min-w-[200px]">
            <label for="email-otp-disable-password"
              class="block text-xs font-medium text-gray-700 mb-1 dark:text-gray-300">Current Password</label>
            <app-input-field id="email-otp-disable-password" type="password" name="emailOtpDisablePassword"
              placeholder="Enter password to disable Email OTP" className="w-full" [value]="emailOtpDisablePassword"
              (valueChange)="emailOtpDisablePassword = $event.toString()" [disabled]="emailOtpDisabling" />
          </div>
          <div class="pb-[1px]">
            <app-button variant="outline" size="sm"
              className="text-red-600 border-red-200 hover:bg-red-50 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-900/20"
              (btnClick)="disableEmailOtp()" [disabled]="emailOtpDisabling">
              @if (emailOtpDisabling) {
              <span class="flex items-center gap-2">
                Disabling...
              </span>
              } @else {
              Disable Email OTP
              }
            </app-button>
          </div>
        </div>
        } @else {
        <div
          class="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-xs text-amber-700 dark:border-amber-500/30 dark:bg-amber-900/20 dark:text-amber-200">
          Your account requires a second verification method. Disabling Email OTP is not available right now.
        </div>
        }
      </div>
      }
    </div>

  </div>

  <app-modal [isOpen]="isEmailOtpPromptOpen" (close)="cancelEmailOtpPrompt()" className="max-w-md"
    [showCloseButton]="!emailOtpEnabling">
    <div class="space-y-4">
      <div class="space-y-1">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white/90">Enable Email OTP</h3>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Add email one-time password as your account's second verification method.
        </p>
      </div>

      @if (!setupToken.trim()) {
      <div>
        <label for="email-otp-enable-password"
          class="block text-xs font-medium text-gray-700 mb-1 dark:text-gray-300">Current Password</label>
        <app-input-field id="email-otp-enable-password" type="password" name="emailOtpPassword"
          placeholder="Enter your current password" className="w-full" [value]="emailOtpPassword"
          (valueChange)="emailOtpPassword = $event.toString()" [disabled]="emailOtpEnabling" />
      </div>
      }

      <div class="flex items-center justify-end gap-3">
        <button type="button" (click)="cancelEmailOtpPrompt()"
          class="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          [disabled]="emailOtpEnabling">
          Cancel
        </button>
        <app-button size="sm" (btnClick)="startEmailOtpSetup()"
          [disabled]="emailOtpEnabling || (!setupToken.trim() && !emailOtpPassword.trim())">
          @if (emailOtpEnabling) {
          <span class="flex items-center gap-2">
            <span class="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
            Enabling...
          </span>
          } @else {
          Enable Email OTP
          }
        </app-button>
      </div>
    </div>
  </app-modal>
</div>